/* global bootstrap, Fancybox, Rellax, AOS */

/**
 * Checks whether the user has requested reduced motion at the OS/browser level. Used to skip
 * JS-driven motion (autoplay, parallax) that a CSS-only kill switch can't reach.
 * @returns {boolean} True when the user prefers reduced motion.
 */
function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Calculates the outer height of an element (content + padding + margin).
 * @param {HTMLElement} el Element to measure.
 * @returns {number} Outer height in pixels.
 */
function completeHeight(el) {
    const curStyle = el.currentStyle || window.getComputedStyle(el);
    let outerHeight = el.offsetHeight;
    outerHeight += parseInt(curStyle.marginTop);
    outerHeight += parseInt(curStyle.marginBottom);
    return outerHeight;
}

/**
 * Fixes elements with the `.fix-on-scroll` class to the top of the viewport once the page
 * scrolls past their original position, compensating for the header height.
 * @returns {void}
 */
function fixOnScroll() {
    const allElements = document.querySelectorAll('.fix-on-scroll'),
        windowTop = window.pageYOffset || document.body.scrollTop,
        nav = document.querySelector('#header'),
        navHeight = completeHeight(nav);

    allElements.forEach(el => {
        const elHeight = completeHeight(el);
        if (windowTop > el.offsetTop && windowTop > el.getAttribute('data-offset-top')) {
            el.classList.add('scroll-fixed');
            el.style.top = `${navHeight}px`;
            el.nextElementSibling.style.marginTop = `${elHeight}px`;
        } else {
            el.classList.remove('scroll-fixed');
            el.nextElementSibling.style.marginTop = '0';
        }
    });
}

if (document.querySelector('.fix-on-scroll') !== null) {
    let allElements = document.querySelectorAll('.fix-on-scroll');
    allElements.forEach(el => {
        el.setAttribute('data-offset-top', el.offsetTop);
    });
    window.addEventListener('load', fixOnScroll);
    window.addEventListener('scroll', fixOnScroll);
    window.addEventListener('resize', fixOnScroll);
}

var swiperTranslations = {
    firstSlideMessage: 'Esse é o primeiro slide',
    lastSlideMessage: 'Esse é o último slide',
    nextSlideMessage: 'Próximo Slide',
    paginationBulletMessage: 'Ir para o slide {{index}}',
    prevSlideMessage: 'Slide Anterior'
};

/**
 * Smoothly scrolls to an element by id, compensating for the fixed header's height. No-ops (and
 * returns false) when the target doesn't exist on the current page - happens when a ".scrollTo"
 * link to a section that only exists on another page (e.g. a menu anchor to the homepage) is
 * clicked from where it isn't rendered, in which case the link's own href should navigate there
 * instead of being blocked by a scroll to nothing.
 * @param {string} id Target element's id, with or without the leading "#".
 * @returns {boolean} True when the element was found and scrolled to.
 */
function scrollToEl(id) {
    if (id[0] !== '#') {
        id = `#${id}`;
    }

    const element = document.querySelector(`${id}`);
    if (element === null) {
        return false;
    }

    const headerCompensate = completeHeight(document.querySelector('#header'));
    element.style.scrollMarginTop = `${headerCompensate + 30}px`;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
}

const buttonScroll = document.querySelectorAll('.scrollTo');
buttonScroll.forEach(trigger => {
    trigger.onclick = e => {
        const id = trigger.getAttribute('data-target') || trigger.getAttribute('href');
        if (scrollToEl(id)) {
            e.preventDefault();
        }
    };
});

/**
 * Sets the accessible container message for a Swiper instance and returns the shared
 * translations object used by every Swiper's `a11y` option.
 * @param {string} msg Accessible label describing the specific Swiper container (e.g. "Banner Principal").
 * @returns {object} The shared Swiper a11y translations object, with `containerMessage` set.
 */
// eslint-disable-next-line no-unused-vars -- usado pelos JS de página (inicial, instagram, slides, noticias-single)
function setSwiperMessage(msg) {
    swiperTranslations.containerMessage = msg;
    return swiperTranslations;
}

/**
 * Binds Fancybox to every element with a `data-fancybox` attribute.
 * @returns {void}
 */
function initFancybox() {
    Fancybox.bind('[data-fancybox]', {
        //
    });
}

if (document.querySelector('[data-fancybox]') !== null) {
    initFancybox();
}

/**
 * Enables Bouncer form validation on every `[data-validate]` form, with the project's
 * Portuguese error messages.
 * @returns {void}
 */
function initFormValidation() {
    // eslint-disable-next-line
    new Bouncer('[data-validate]', {
        messages: {
            missingValue: {
                checkbox: 'Este campo é obrigatório.',
                radio: 'Por favor selecione uma opção.',
                select: 'Por favor selecione uma opção.',
                'select-multiple': 'Por favor selecione ao menos uma opção.',
                default: 'Por favor preencha este campo.'
            },
            patternMismatch: {
                email: 'Por favor insira um email válido.',
                url: 'Por favor entre uma URL válida.',
                number: 'Por favor entre um número.',
                color: 'Por favor entre com o seguinte formato: #rrggbb',
                date: 'Por favor entre a data no formato DD-MM-YYYY.',
                time: 'Por favor use o formato de 24 horas. Ex.: 23:00',
                month: 'Por favor entre o formato MM-YY',
                default: 'Por favor preencha o campo no formato requerido.'
            },
            outOfRange: {
                over: 'Por favor selecione um valor abaixo de {max}.',
                under: 'Por favor selecione um valor acima de {min}.'
            },
            wrongLength: {
                over: 'O texo não pode passar de {maxLength} caracteres.',
                under: 'O texo precisa ter no mínimo {minLength} caracteres.'
            }
        }
    });
}

if (document.querySelectorAll('[data-validate]').length > 0) {
    initFormValidation();
}

/**
 * Updates the CSS background position of every `.parallax` element based on scroll position,
 * creating a parallax scrolling effect.
 * @returns {void}
 */
function parallax() {
    document.querySelectorAll('.parallax').forEach(e => {
        let yPos = -((window.pageYOffset - e.offsetTop) / e.getAttribute('data-speed'));
        let bgpos = '50% '+ yPos + 'px';
        e.style.backgroundPosition = bgpos;
    });
}

if (document.querySelectorAll('.parallax').length && !prefersReducedMotion()) {
    window.addEventListener('load', parallax);
    window.addEventListener('scroll', parallax);
}

/**
 * Initializes Bootstrap tooltips on every element with a `data-bs-toggle="tooltip"` attribute.
 * @returns {void}
 */
function initTooltips() {
    // Também pega elementos com data-bs-title mas sem data-bs-toggle="tooltip" - caso de um
    // elemento que já usa data-bs-toggle pra outro comportamento (ex. "modal") e não pode
    // sobrescrever esse valor só pra ganhar o tooltip.
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"], [data-bs-title]');
    [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
}

if (document.querySelectorAll('[data-bs-toggle="tooltip"], [data-bs-title]').length > 0) {
    initTooltips();
}

/**
 * Initializes the AOS (Animate On Scroll) library with the project's default options.
 * @returns {void}
 */
function initAOS() {
    AOS.init({
        disable: prefersReducedMotion() ? true : 'mobile',
        once: true
    });
}

initAOS();

/**
 * Initializes the Rellax parallax library on every `.rellax` element.
 * @returns {void}
 */
function initRellax() {
    new Rellax('.rellax', {
        breakpoints: [576, 768, 1200]
    });
}

if (document.querySelector('.rellax') !== null) {
    initRellax();
}
